import { merge } from "lodash-es";
import React, { useContext, useState, useMemo } from "react";
import Cookie from "../Cookie";
import useQueryParams from "../useQueryParams";
import "reactjs-json-editor/css/style.css";
import { withDCS3Provider } from "../../api/dcs3";
import useUserRepository from "../../api/dcs3/useUserRepository";
import {
	useGetAllVehicleSubscriptions,
	useGetSafetyCampaigns,
	useGetUserVehicles,
	useGetVehicleGuides,
	useGetVehicleHealthReport,
	useGetVehicleManuals,
	useGetVehicleServiceHistory,
	useVehicleSpecifications,
	withEOSProvider,
} from "../../api/eos";
import { getFeatures } from "../../api/eos/productMap";

export const PersonalizedContext = React.createContext({});

function getModelFromTrim(trim = "") {
	return trim?.replace(/([0-9\s]+)/g, "");
}

export const useEosApi = (userData) => {
	const isInitialLoadingUser = userData?.isInitialLoadingUser;
	const [eosData, setEosData] = useState({ isInitialLoadingUser });
	const hasUserData =
		Object.keys(userData || {}).length > 0 &&
		(typeof userData.firstName !== "undefined" ||
			typeof userData.organizationName !== "undefined");

	const isLoggedIn = useIsLoggedIn(userData);
	const {
		vehicles,
		isInitialLoadingUserVehicles,
		status: eosStatus,
	} = useGetUserVehicles(hasUserData);
	const {
		year = "",
		model = "",
		vin = "",
	} = useDriversPreferredVehicle(hasUserData);

	const [cookie, setCookie] = Cookie("drivers-default-vehicle");
	const defaultVehicleObject = cookie
		? {}
		: {
				defaultVehicleString: `${year} ${model}`,
				defaultVehicle: {
					year,
					model: getModelFromTrim(model),
					trim: model,
					vin,
				},
			};
	const hasImage = vehicles?.[0]?.jellyImage;
	useMemo(() => {
		if (!isLoggedIn) {
			return;
		}
		setEosData({
			isLoggedIn: isLoggedIn,
			isInitialLoadingUser,
			isInitialLoadingUserVehicles,
			eosStatus,
			vehicles: vehicles.map((v) => ({
				make: v.make,
				model: v.model,
				year: v.year,
				vin: v.vin,
				yearModelTrimString: `${v.year} ${v.model}`,
				vehicleId: v.vehicleId,
				nickName: v.nickName,
				preferredVehicle: v.preferredVehicle,
				exteriorColorCode: v?.exteriorColorCode,
				jelly: v?.jellyImage?.vehicleImage,
				banner: v?.banner?.desktopImage,
				homeDealerCode: v?.homeDealerCode,
				preferredDealer: v?.preferredDealer,
				preferredDealerCode: v?.preferredDealerCode,
				driveType: v?.driveType,
				description: v?.description,
			})),
			...defaultVehicleObject,
		});
	}, [
		isLoggedIn,
		vehicles.length,
		hasImage,
		year,
		model,
		vin,
		isInitialLoadingUser,
		isInitialLoadingUserVehicles,
	]);
	return eosData;
};

export const PersonalizationProvider = withEOSProvider(
	withDCS3Provider(({ children, ...props }) => {
		const params = useSetupParams();
		const [id_token] = Cookie("id_token");
		const [id_token_local] = Cookie("id_token_local");
		const [id_token_tmnab2cqa] = Cookie("id_token_tmnab2cqa");
		const { user: data = {}, isInitialLoadingUser } = useUserRepository(
			typeof (id_token || id_token_local || id_token_tmnab2cqa) !== "undefined",
		);
		const eosData = useEosApi({ ...data, isInitialLoadingUser });

		const [cookie, setCookie] = Cookie("drivers-default-vehicle");
		const [defaultVehicle, setDefaultVehicle] = useState(cookie);
		const [year, ...modelArr] = (defaultVehicle || "").split(" ");
		const [trim, vin = null] = (modelArr?.join(" ") || "").split("/");
		const model = getModelFromTrim(trim);
		const user = {
			// personal: data,
			firstName: data?.firstName,
			lastName: data?.lastName,
			organizationName: data?.organizationName,
			phone: data?.telephoneNumber,
			customerType: data?.customerType,
			address: data?.postalAddresses?.[0],
			customerCentralId: data?.customerCentralId,
			tmsGuid: data?.tmsGuid,
			defaultVehicle: {},
			defaultVehicleString: "",
		};
		const userDefaultVehicle = cookie
			? {
					defaultVehicleString: (defaultVehicle || "").split("/")[0],
					defaultVehicle: {
						year,
						trim,
						model,
						vin: id_token ? vin : undefined,
					},
				}
			: {};

		const [debuggerData, setDebuggerData] = useState({});
		const formattedParams = {
			...params,
		};
		if (params["defaultVehicleString"]) {
			const [year, trim] = params["defaultVehicleString"].split(" ");
			formattedParams.defaultVehicle = {
				year,
				trim,
				model: getModelFromTrim(trim),
			};
		}
		if (params["defaultVehicle"]) {
			formattedParams.defaultVehicleString = `${
				params["defaultVehicle"]["year"]
			} ${
				params["defaultVehicle"]["trim"] || params["defaultVehicle"]["model"]
			}`;
			if (
				!params["defaultVehicle"]["model"] &&
				params["defaultVehicle"]["trim"]
			) {
				formattedParams.defaultVehicle.model = getModelFromTrim(
					params["defaultVehicle"]["trim"],
				);
			}
		}

		const mergedData = merge(
			props,
			user,
			eosData,
			userDefaultVehicle,
			formattedParams,
			debuggerData,
		);
		const functions = {
			setDefaultVehicle: (v, vin) => {
				const decodedTrim = decodeURIComponent(v);
				const value = vin ? `${decodedTrim}/${vin}` : decodedTrim;
				setDefaultVehicle(value);
				setCookie(value);
			},
		};
		window.onpagehide = function () {
			typeof (id_token || id_token_local) !== "undefined" && setCookie("");
		};

		return (
			<PersonalizedContext.Provider value={{ data: mergedData, functions }}>
				{children}
			</PersonalizedContext.Provider>
		);
	}),
);

export const usePersonalizedContext = () =>
	useContext(PersonalizedContext).data;

export const usePersonalizationFunctions = () => {
	return useContext(PersonalizedContext).functions || {};
};

export const Debugger = (props) => {
	const { debug } = useQueryParams()?.p || {};
	const [showing, setShowing] = useState(false);

	return (
		debug === "true" && (
			<>
				<button
					onClick={() => setShowing(!showing)}
					style={{
						position: "absolute",
						right: "10px",
						cursor: "pointer",
						zIndex: "99999",
					}}
				>
					{showing ? "close" : "debug"}
				</button>
				{showing && (
					<div
						style={{
							position: "fixed",
							bottom: "0",
							zIndex: "9999",
							background: "rgba(0,0,0,0.8)",
							color: "#FFF",
							padding: "10px",
							left: "0",
							height: "100%",
							boxSizing: "border-box",
							maxWidth: "90%",
							overflow: "scroll",
						}}
					>
						{/* <JSONEditor
            value={props}
          /> */}
						<pre style={{ fontSize: "14px" }}>
							{JSON.stringify(props, " ", 2)}
						</pre>
					</div>
				)}
			</>
		)
	);
};

export const PersonalizationContextBindingProvider = ({
	children,
	...props
}) => {
	// const proxyProps = {
	//   authorizationType: 'authenticated',
	//   excludedModelYears: [{model: "LC", year: "2021"}],
	// }
	/* eslint-disable-next-line */
	const { apiContext, hideEmptyContext = false, ...extras } = props;

	const isAuth = useIsAuthenticated(props);
	const { data, functions } = useContext(PersonalizedContext);
	const apiData = usePersonalizedApiCall(apiContext)() || {};
	const hasData = apiData._isEmpty !== true;
	const onlyRenderIfApiDataHasData =
		hideEmptyContext && apiContext && !apiData?._isLoading ? hasData : true;
	const render = isAuth && onlyRenderIfApiDataHasData;

	if (!render) {
		return <></>;
	}
	const mergedData = {
		...data,
		...(apiData || {}),
		apiContext,
	};

	return (
		<PersonalizedContext.Provider value={{ functions, data: mergedData }}>
			<Debugger {...mergedData} />
			{children}
		</PersonalizedContext.Provider>
	);
};

export const useGarageVehicles = () => {
	const { vehicles = [] } = usePersonalizedContext() || {};
	return vehicles || [];
};

export const useDriversPreferredVehicle = (hasUserData) => {
	const { vehicles } = useGetUserVehicles(hasUserData);
	return vehicles.find((i) => i.preferredVehicle === "Y") || {};
};

export const useDefaultVehicle = () => {
	const {
		defaultVehicle,
		defaultVehicleString = undefined,
		isInitialLoadingUserVehicles,
	} = usePersonalizedContext() || {};
	return { defaultVehicle, defaultVehicleString, isInitialLoadingUserVehicles };
};

export const useIsLoggedIn = (userData = {}) => {
	const data = usePersonalizedContext() || userData;
	// const [cookie] = Cookie('isLoggedIn'); // Cookie stays true even when session expires, so shouldn't be used
	return (
		(typeof data.firstName !== "undefined" && data.firstName !== "") ||
		(typeof data.organizationName !== "undefined" &&
			data.organizationName !== "")
	);
};

export function findVehicleInList(vehicle, list = [], preferred = false) {
	return list.find((i) =>
		preferred
			? String(i.year) === String(vehicle?.year) &&
				(i.model?.toLowerCase() === vehicle?.model?.toLowerCase() ||
					i.model === "*") &&
				i.preferredVehicle === "Y"
			: String(i.year) === String(vehicle?.year) &&
				(i.model?.toLowerCase() === vehicle?.model?.toLowerCase() ||
					i.model === "*"),
	);
}

/// These props should be changed so they don't overlap
export const useIsAuthenticated = ({
	authorizationType,
	modelYears = [],
	excludedModelYears = [],
}) => {
	const { vehicles = [] } = usePersonalizedContext() || {};
	const { defaultVehicle } = useDefaultVehicle();
	const isAuthenticated = useIsLoggedIn();
	const { editorMode = false } = window || {};
	if (editorMode) {
		return true;
	}

	if (modelYears.length > 0) {
		const whitelist = findVehicleInList(defaultVehicle, modelYears);
		if (!whitelist) {
			return false;
		}
	}
	if (excludedModelYears.length > 0) {
		const blacklist = findVehicleInList(defaultVehicle, excludedModelYears);
		if (blacklist) {
			return false;
		}
	}

	if (authorizationType === "authenticated") {
		if (!isAuthenticated) {
			return false;
		}
		return true;
	}
	if (authorizationType === "no-vehicles") {
		// defaultVehicle from users garage or from model year form
		return !defaultVehicle?.year && !defaultVehicle?.trim;
	}
	if (authorizationType === "unauthenticated" && isAuthenticated) {
		return false;
	}

	return true;
};

/* Context Strings Below */
export const useDefaultHook = () => ({});

export const useVehicleHealthReportContext = () => {
	const { vin = "" } = usePersonalizedContext().defaultVehicle;
	const { isLoading, data } = useGetVehicleHealthReport(vin);
	const {
		maintenanceInformation,
		vehicleStatus,
		safetyRecalls,
		serviceCampaigns,
	} = data || {};
	if (!maintenanceInformation) {
		return {
			_isEmpty: true,
		};
	}

	const {
		currentMileageUnit,
		currentMileage,
		currentServiceMileage,
		currentServiceMileageUnit,
		currentServiceTime,
		currentServiceTimeUnit,
	} = maintenanceInformation;

	const {
		milesToEmptyDescription,
		engineOilLevelDescription,
		oilDegradationDescription,
		oilDegradationStatus,
		engineOilLevelStatus,
	} = vehicleStatus;

	const formattedCurrentMileage = currentMileage?.toLocaleString?.("en-US");
	const formattedCurrentServiceMileage = Number(
		currentServiceMileage,
	)?.toLocaleString?.("en-US");

	const oilQuality = (status) => {
		let result;
		if (status === 0) {
			result = "POOR";
		} else if (status > 0 && status <= 2) {
			result = "GOOD";
		} else if (status === 3) {
			result = "EXCELLENT";
		}
		return result;
	};

	const engineLevel = (status) => {
		let result;
		if (status === 0) {
			result = "LOW";
		} else if (status > 0 && status <= 2) {
			result = "MID";
		} else if (status === 3) {
			result = "FULL";
		}
		return result;
	};

	return {
		_isLoading: isLoading,
		_isEmpty: false,
		vhrSummary: {
			milesToEmpty: milesToEmptyDescription,
			milesDriven:
				`${formattedCurrentMileage} ${currentMileageUnit}s`.toUpperCase(),
			recommendedServiceMiles:
				`${formattedCurrentServiceMileage} ${currentServiceMileageUnit}`.toUpperCase(),
			recommendedServiceTime:
				`${currentServiceTime} ${currentServiceTimeUnit}`.toUpperCase(),
			numOfSafetyRecalls: safetyRecalls?.safetyRecallList.length || 0,
			numOfServiceCampaigns: serviceCampaigns?.serviceCampaignsList.length || 0,
			engineOilLevelDescription,
			engineOilLevelStatus: engineLevel(engineOilLevelStatus),
			oilDegradationDescription,
			oilDegradationStatus: oilQuality(oilDegradationStatus),
			currentMileage,
		},
		vhr: {
			...maintenanceInformation,
			...vehicleStatus,
		},
	};
};

export const useVehicleServiceHistoryContext = () => {
	const { vin = "" } = usePersonalizedContext().defaultVehicle;
	const { data = {}, isLoading } = useGetVehicleServiceHistory(vin);
	const { serviceHistoryRecords, repairOrders } = data;
	const consolidateData = [
		...serviceHistoryRecords.map((i) => ({
			editId: i.memberServiceHistoryId,
			_miles: i.mileage,
			miles: `${i.mileage} miles`,
			date: new Date(i.serviceDate?.replaceAll("-", "/")).toLocaleDateString(),
			repairNum: i.repairOrderNum,
			reportedBy: i.serviceProvider,
			services: i.memberServiceHistoryDetails.map((j) => (
				<p>
					<b>SERVICE: </b>
					{j.serviceCategory}
					<br />
					<b>DESCRIPTION:</b> {j.serviceCategoryDesc}
					{j.serviceOther && (
						<div>
							<b>COMMENTS:</b> {j.serviceOther}
						</div>
					)}
				</p>
			)),
			_data: i,
		})),
		...repairOrders.map((i) => ({
			_miles: i.miles,
			miles: `${i.miles} miles`,
			date: new Date(i.postedDate).toLocaleDateString(),
			repairNum: i.repairOrderNumber,
			reportedBy: i?.dealer?.dealerName,
			services: i.repairOrderConditions.map((j) =>
				j.operation.map((k) => (
					<p>
						<b>SERVICE: </b>
						{k.opCodeDescription}
						<br />
						<b>DESCRIPTION:</b>{" "}
						{k.serviceOperationDescription.replace(k.opCodeDescription, "")}
					</p>
				)),
			),
			_data: i,
		})),
	].sort((a, b) => parseInt(b._miles) - parseInt(a._miles));

	return {
		_isLoading: isLoading,
		_isEmpty: !(consolidateData?.length > 0),
		serviceHistoryRecords: consolidateData || [],
	};
};

function transformVehicleManuals(manuals, path, vehicleData) {
	const vehicleAppend = vehicleData
		? `&model=${vehicleData.model}&year=${vehicleData.year}`
		: "";
	if (!manuals) {
		return [];
	}
	const manualFormats = {
		PDF: [],
		HTML: [],
	};
	manuals.forEach((manual) =>
		manualFormats[manual.documents[0].format].push({
			link:
				manual.documents[0].format === "PDF"
					? manual.documents[0].contentLink
					: path + manual.pubNumber + vehicleAppend,
			title: manual.pubTitle.toUpperCase(),
			pubNumber: manual.pubNumber,
		}),
	);
	return manualFormats;
}

const useSetupParams = () => {
	const queryParams = useQueryParams() || {};
	const params = { ...queryParams?.p };

	if (queryParams.model && queryParams.year) {
		params.defaultVehicle = {
			trim: queryParams.model,
			year: queryParams.year,
		};
	}
	return params;
};

export const useVehicleManualsContext = (
	path = "/drivers/resources/manual?pubNumber=",
) => {
	const params = useSetupParams();
	const personalizationContext = usePersonalizedContext();
	const { trim, year, vin } = params.defaultVehicle
		? params.defaultVehicle
		: personalizationContext?.defaultVehicle;
	const { data: manuals, isFetching } = useGetVehicleManuals({
		model: trim,
		year,
		vin,
	});

	const ret = useMemo(() => {
		return manuals
			? {
					_isLoading: false,
					warrantyGuides: transformVehicleManuals(
						manuals.warrantyGuides,
						path,
						{ model: trim, year },
					),
					omotaOwnersManualOverTheAir: transformVehicleManuals(
						manuals.omotaOwnersManualOverTheAir,
						path,
						{ model: trim, year },
					),
					navigationManuals: transformVehicleManuals(
						manuals.navigationManuals,
						path,
						{ model: trim, year },
					),
					ownerManuals: transformVehicleManuals(manuals.ownerManuals, path, {
						model: trim,
						year,
					}),
				}
			: {
					_isEmpty: !manuals,
					_isLoading: isFetching,
				};
	}, [trim, year, vin, isFetching, manuals]);

	return ret;
};

export const useMyLexusVehicleManualsContext = () => {
	return useVehicleManualsContext("/My-Lexus/resources/manual?pubNumber=");
};

export const useTechQuickGuidesContext = () => {
	const { model, year } = usePersonalizedContext()?.defaultVehicle;
	const { data } = useGetVehicleGuides({ model, year });
	const responseCode = data?.data?.status;

	if (responseCode !== 204) {
		if (data?.technologyGuideSource) {
			return {
				techQuickGuide: data?.technologyGuideSource,
			};
		}
		return {
			_isEmpty: true,
		};
	}
	return {
		_isEmpty: true,
	};
};

export const useVehicleGuides = () => {
	const { model, year } = usePersonalizedContext()?.defaultVehicle;
	const { data } = useGetVehicleGuides({ model, year });
	const responseCode = data?.data?.status;
	return responseCode !== 204
		? {
				techQuickGuide: data?.technologyGuideSource,
				lps: data?.lpsSource,
			}
		: {
				_isEmpty: true,
			};
};

function mergeSafetyRecallCampaigns(recalls, campaigns = []) {
	const recallItems = (recalls?.safetyRecallList || []).map((i) => ({
		title: i.Title,
		description: "",
		date: new Date(i.CampaignDate).toLocaleDateString(),
		_data: i,
	}));
	const campaignItems = campaigns.map((i) => ({
		title: i.campaignTitle,
		description: i.activityDescription,
		date: new Date(i.releaseDate).toLocaleDateString(),
		_data: i,
	}));
	return [...recallItems, ...campaignItems];
}

export const useSafetyServiceAlertsContext = () => {
	const { vin = "" } = usePersonalizedContext().defaultVehicle;
	const { isLoading, data } = useGetVehicleHealthReport(vin);
	const { data: campaing } = useGetSafetyCampaigns(vin);
	const safetyCampaigns = campaing?.vehicleCampaignList || [];
	const {
		vehicleAlerts: vA,
		safetyRecalls: sR,
		serviceCampaigns: sC,
	} = data || {};

	const safetyRecallCampaigns = mergeSafetyRecallCampaigns(sR, safetyCampaigns);
	const vehicleAlerts = (vA?.vehicleAlertList || []).map((i) => ({
		name: i.WNGName,
		title: i.WNGName,
		date: new Date(i.WNGDate).toLocaleDateString(),
		description: i.WNGDesc,
	}));

	const safetyRecalls = sR?.safetyRecallList || [];

	const serviceCampaigns = sC?.serviceCampaignsList || [];

	if (!vehicleAlerts && !safetyRecalls && !serviceCampaigns) return {};

	return {
		_isLoading: isLoading,
		_isEmpty: !(
			!!vehicleAlerts.length &&
			!!safetyRecalls.length &&
			!!serviceCampaigns.length
		),
		safetyServiceAlerts: {
			numOfVehicleAlerts: vehicleAlerts.length,
			numOfSafetyRecalls: safetyRecalls.length,
			numOfServiceCampaigns: serviceCampaigns.length,
			numOfsafetyCampaigns: safetyCampaigns.length,
			numOfSafetyRecallCampaigns: safetyRecallCampaigns.length,
			vehicleAlerts,
			safetyRecalls,
			serviceCampaigns,
			safetyCampaigns,
			safetyRecallCampaigns,
		},
	};
};

export const useAllVehicleSubscriptionsContext = () => {
	const { vin = "" } = usePersonalizedContext().defaultVehicle;
	const { data, isLoading } = useGetAllVehicleSubscriptions() || {};
	const vinSubscription = data?.[vin];
	const features = getFeatures(vinSubscription?.sxmProductList);
	return {
		_isLoading: isLoading,
		vinSubscription,
		features,
	};
};

export const useHasWifiContext = () => {
	const { vin = "" } = usePersonalizedContext().defaultVehicle;
	const { isLoading, data } = useVehicleSpecifications(vin);
	const { VehicleSpecDetailResponse } = data || {};
	const enabled = VehicleSpecDetailResponse?.hotSpotWifiCapability === "Y";

	return {
		_isLoading: isLoading,
		_isEmpty: !enabled,
		hasWifi: enabled,
		wifi: {
			enabled,
			imei: VehicleSpecDetailResponse?.imei,
			meid: VehicleSpecDetailResponse?.meid,
		},
	};
};

export const useHasDefaultVehicleWithVin = () => {
	const { vin = "" } = usePersonalizedContext().defaultVehicle;
	const hasVin = vin.length > 0;
	return {
		_isEmpty: !hasVin,
		hasVin,
	};
};

export const useVhrAndServiceHistoryContext = () => {
	const vhrData = useVehicleHealthReportContext();
	const serviceHistoryData = useVehicleServiceHistoryContext();
	return {
		...vhrData,
		...serviceHistoryData,
		_isEmpty: vhrData?._isEmpty && serviceHistoryData?._isEmpty,
		_isLoading: false,
	};
};

export const usePersonalizedApiCall = (context) => {
	switch (context) {
		case "vehicle-manuals":
			return useVehicleManualsContext;
		case "mylexus-vehicle-manuals":
			return useMyLexusVehicleManualsContext;
		case "vehicle-service-history":
			return useVehicleServiceHistoryContext;
		case "vehicle-health-report":
			return useVehicleHealthReportContext;
		case "safety-service-alerts":
			return useSafetyServiceAlertsContext;
		case "subscriptions":
			return useAllVehicleSubscriptionsContext;
		case "has-wifi":
			return useHasWifiContext;
		case "has-vin":
			return useHasDefaultVehicleWithVin;
		case "vehicle-guides":
			return useVehicleGuides;
		case "tech-quick-guides":
			return useTechQuickGuidesContext;
		case "vhr-and-service-history":
			return useVhrAndServiceHistoryContext;
		default:
			return useDefaultHook;
	}
};
